import { useEffect, useRef, useState } from "react"
import axios from "axios"
import nProgress from "nprogress"

import useCustomerPickerRepositories from "repositories/customer-picker"

// 270726 (permintaan user): pencarian customer bisa lewat KODE customer (mis. "BD 01").
// Kode hanya ada di mapping Master Customer HO (customer_master_mapping), tidak di
// daftar customer POS — jadi picker menembak DUA sumber lalu menggabungkannya:
//   1. daftar customer POS (perilaku lama: nama / telepon)
//   2. lookup Master Customer HO (kode / nama / telepon / id)
// Baris dari kedua sumber dipadankan lewat id customer yang sama, dan hasil lookup
// juga dipakai menambahkan kolom Kode + diskon default ke baris dari POS.
const inventoryBase = () => (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

export default function CustomerPickerModal({
  open,
  onClose,
  onSelect,
}) {
  const searchInput = useRef(null)
  const debounceRef = useRef(null)

  const [search, setSearch] = useState("")
  const [committedSearch, setCommittedSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data, isLoading } = useCustomerPickerRepositories(
    {
      page,
      search: committedSearch || undefined,
      pagination: true,
      // 200726: alamat customer ikut dikirim (prefill alamat pengiriman order HO).
      with_address: 1,
    },
    {
      enabled: open,
    }
  )

  const customerData = data ?? {}

  useEffect(() => {
    if (!open) return

    setSearch("")
    setCommittedSearch("")
    setPage(1)
  }, [open])

  useEffect(() => {
    if (!open) return
    searchInput.current?.focus()
  }, [open])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Hasil pencarian dari Master Customer HO (bisa by KODE) — sumber kedua picker.
  const [masterRows, setMasterRows] = useState([])
  // Peta id → data master (kode & diskon default) untuk melengkapi baris dari POS.
  const [masterById, setMasterById] = useState({})

  useEffect(() => {
    if (!open || !committedSearch) {
      setMasterRows([])
      return
    }

    const ctrl = new AbortController()
    axios
      .get(`${inventoryBase()}/customer-master/lookup`, {
        params: { search: committedSearch, limit: 25 },
        signal: ctrl.signal,
      })
      .then((res) => setMasterRows(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(() => setMasterRows([]))

    return () => ctrl.abort()
  }, [open, committedSearch])

  const posRows = customerData?.data ?? []
  const posIdKey = posRows.map((r) => r?.id).join(",")

  useEffect(() => {
    if (!open || !posIdKey) {
      setMasterById({})
      return
    }

    const ctrl = new AbortController()
    axios
      .get(`${inventoryBase()}/customer-master/lookup`, {
        params: { ids: posIdKey },
        signal: ctrl.signal,
      })
      .then((res) => {
        const list = Array.isArray(res?.data?.data) ? res.data.data : []
        setMasterById(Object.fromEntries(list.map((row) => [String(row.id), row])))
      })
      .catch(() => setMasterById({}))

    return () => ctrl.abort()
  }, [open, posIdKey])

  if (!open) return null

  // Hasil master yang TIDAK ada di daftar POS ditaruh paling atas — kalau user
  // mengetik kode, yang dicarinya memang baris itu.
  const posIds = new Set(posRows.map((r) => String(r?.id)))
  const masterOnlyRows = masterRows
    .filter((row) => !posIds.has(String(row.id)))
    .map((row) => ({ ...row, __fromMaster: true }))

  const rows = [...masterOnlyRows, ...posRows]
  const currentPage = customerData?.current_page ?? page
  const lastPage = customerData?.last_page ?? 1
  const from = customerData?.from ?? 0
  const to = customerData?.to ?? 0
  const total = customerData?.total ?? 0

  const updateSearch = (value) => {
    setSearch(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setCommittedSearch(value)
      setPage(1)
    }, 400)
  }

  const commitSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setCommittedSearch(search)
    setPage(1)
  }

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSearch("")
    setCommittedSearch("")
    setPage(1)
    searchInput.current?.focus()
  }

  const getCustomerName = (v) => {
    return (
      v.full_name ||
      v.name ||
      v.customer_name ||
      "-"
    )
  }

  const getCustomerPhone = (v) => {
    return (
      v.phone_number ||
      v.phone ||
      v.mobile_phone ||
      "-"
    )
  }

  // 270726: data master (kode + diskon default) untuk sebuah baris — baris hasil
  // pencarian master sudah membawanya sendiri, baris POS dilengkapi dari peta ids.
  const getMasterInfo = (v) => (v?.__fromMaster ? v : masterById[String(v?.id)] || null)

  // Alamat: baris master menyimpan string alamat; baris POS punya daftar addresses
  // (200726: alamat utama dulu — is_primary sudah diurutkan server, fallback
  // default_address_id, lalu alamat pertama).
  const getCustomerAddress = (v) => {
    if (typeof v?.address === "string" && v.address) return v.address
    const list = Array.isArray(v?.addresses) ? v.addresses : []
    const chosen = list.find((a) => String(a?.id) === String(v?.default_address_id)) || list[0]
    return chosen?.full_address || ""
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Pilih Customer
            </h3>
            <p className="text-sm text-neutral-500">
              Cari berdasarkan kode customer, nama, atau nomor telepon
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="relative">
            <input
              ref={searchInput}
              value={search}
              onChange={(e) => updateSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitSearch()
                }
              }}
              type="text"
              placeholder="Cari kode customer / nama / nomor telepon..."
              className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
            />

            {!!search && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-700"
              >
                ✕
              </button>
            )}
          </div>

          <div className="border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-1">ID</div>
              <div className="col-span-2">Kode</div>
              <div className="col-span-4">Nama</div>
              <div className="col-span-3">Telepon</div>
              <div className="col-span-2 text-right">Aksi</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Memuat data customer...
                </div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Customer tidak ditemukan.
                </div>
              ) : (
                rows.map((item) => {
                  const master = getMasterInfo(item)
                  const discount = Number(master?.default_discount_percent ?? 0)

                  return (
                    <div
                      key={`${item.__fromMaster ? "m" : "p"}-${item.id}`}
                      className="grid grid-cols-12 gap-4 px-4 py-4 border-b last:border-b-0 items-center"
                    >
                      <div className="col-span-1 text-sm text-neutral-600">
                        {item.id}
                      </div>

                      <div className="col-span-2 font-mono text-xs text-neutral-600">
                        {master?.kode || "-"}
                      </div>

                      <div className="col-span-4">
                        <div className="font-medium text-neutral-800">
                          {getCustomerName(item)}
                        </div>

                        {!!item.email && (
                          <div className="text-xs text-neutral-500">
                            {item.email}
                          </div>
                        )}

                        {discount > 0 && (
                          <div className="text-[11px] text-emerald-700">
                            Diskon default {discount}%
                          </div>
                        )}
                      </div>

                      <div className="col-span-3 text-sm text-neutral-700">
                        {getCustomerPhone(item)}
                      </div>

                      <div className="col-span-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            onSelect({
                              id: item.id,
                              name: getCustomerName(item),
                              phone: getCustomerPhone(item),
                              address: getCustomerAddress(item),
                              // 270726: kode & diskon default customer ikut dikirim —
                              // form Order HO memakai diskonnya sebagai nilai awal.
                              kode: master?.kode ?? null,
                              default_discount_percent: discount,
                              raw: item,
                            })
                          }
                          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700"
                        >
                          Pilih
                        </button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-neutral-500">
              {total > 0
                ? `Menampilkan ${from}-${to} dari ${total} customer`
                : "Tidak ada data"}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="px-4 py-2 text-sm border rounded-xl border-neutral-200 disabled:opacity-50"
              >
                Sebelumnya
              </button>

              <div className="px-3 text-sm text-neutral-600">
                {currentPage} / {lastPage}
              </div>

              <button
                type="button"
                disabled={currentPage >= lastPage}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-4 py-2 text-sm border rounded-xl border-neutral-200 disabled:opacity-50"
              >
                Berikutnya
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}